"use client";

import React from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { Button } from "@/components/ui/button";
import Image from '@/components/ui/Image';
import Link from "next/link";
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";
import { Badge } from "@/components/ui/badge";

interface Feature19Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  badge?: string;
  title?: string; 
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  secondaryButton?: boolean;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
  secondaryButtonStyle?: ButtonStyleType;
  leftImage?: string; 
  leftImageAltText?: string;  
  rightTopImage?: string; 
  rightTopImageAltText?: string;  
  rightBottomImage?: string; 
  rightBottomImageAltText?: string;  
  withTable?: boolean;
  table?: {
    tableTitle?: string;
    tableHeader?: {
      col: string;
    }[];
    tableHeaderIcon?: {
      icon: string;
    }[];
    row?: {
      col: {
        col: string;
      }[];
    }[];  
  };
  attributes?: React.HTMLProps<HTMLElement>;
}

const TableBlock = (table: {
  tableTitle?: string;
  tableHeader?: {
    col: string;
  }[];
  tableHeaderIcon?: {
    icon: string;
  }[];
  row?: {
    col: {
      col: string;
    }[];
  }[];  
}) => {
  if (!table) return null;
  return (
    <div className="mt-16 lg:mt-20"  data-aos="fade-up" data-aos-delay={100}>
      {table?.tableTitle && (
        <h3 className="mb-6">
          {table?.tableTitle}
        </h3>
      )}
      <div className="lg:hidden flex flex-col gap-0 border-b">
        {table?.row && table.row.length > 0 && (
          table.row.map((row, rowIndex) => (
            <div key={rowIndex} className="flex flex-col gap-4 border-b py-4">
              {row.col?.map((cell, cellIndex) => (
                <div key={cellIndex} className="flex flex-col gap-1">
                  {table?.tableHeader && table.tableHeader.length > 0 && cellIndex !== 0 && (
                    <div className="paragraph-base text-left text-muted-foreground font-normal">{parse(table.tableHeader[cellIndex].col)}</div>
                  )}
                  {cellIndex === 0 ? (
                    <div className="paragraph-lg text-left font-bold flex items-center gap-2 pb-2">
                      {table?.tableHeaderIcon && table.tableHeaderIcon.length > 0 && (
                        <span className="material-symbols-outlined text-brand">
                          {table.tableHeaderIcon[rowIndex].icon}
                        </span>
                      )}
                      <span>{parse(cell.col)}</span>
                    </div>
                  ) : (
                    <div className="paragraph-lg text-left">{parse(cell.col)}</div>
                  )}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
      <table className="table-auto w-full hidden lg:table">
        {table?.tableHeader && table.tableHeader.length > 0 && (
          <thead>
            <tr className="border-b">
              {table.tableHeader.map((cell, cellIndex) => (
                <th key={cellIndex} className="py-4 paragraph-base text-left text-muted-foreground font-normal">{parse(cell.col)}</th>
              ))}
            </tr>
          </thead>
        )}
        {table?.row && table.row.length > 0 && (
          <tbody>
            {table.row.map((row, rowIndex) => (
              <tr key={rowIndex} className="border-b">
                {row.col?.map((cell, cellIndex) => (
                  <td key={cellIndex} className="py-4 paragraph-base text-left">
                    {cellIndex === 0 ? (
                      <div className="flex items-center gap-2 font-bold">
                        {table?.tableHeaderIcon && table.tableHeaderIcon.length > 0 && (
                          <span className="material-symbols-outlined text-brand">
                            {table.tableHeaderIcon[rowIndex].icon}
                          </span>
                        )}
                        {parse(cell.col)}
                      </div>
                    ) : (
                      <div className="paragraph-base text-left">
                        {parse(cell.col)}
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </div>
  );
};

const Feature19 = ({
  darkMode = false,
  variant = "A",
  spacing,
  usedInPageNav = false,
  pageNavTitle = "Feature 19",
  themeChangeOnScroll = false,
  badge,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  secondaryButton,
  secondaryButtonText,
  secondaryButtonLink,
  secondaryButtonStyle,
  leftImage,
  leftImageAltText,
  rightTopImage,
  rightTopImageAltText,
  rightBottomImage,
  rightBottomImageAltText,
  withTable,
  table,
  attributes,
}: Feature19Props) => {  

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="feature19"
      attributes={attributes}
    >
      <div className="container">
        <div className="flex w-full gap-16 lg:gap-20 overflow-hidden flex-col lg:flex-row lg:items-center" data-aos="fade-up">
          <div className="w-full shrink lg:w-1/2 text-center lg:text-left">
            {badge && (
              <Badge variant="outline" className="mb-4">
                {badge}
              </Badge>
            )}
            {title && (
              <h2>
                {parse(title)}
              </h2>
            )}
            {content && (
              <div className="text-muted-foreground paragraph-lg mt-4 lg:mt-8">
                {parse(content)}
              </div>
            )}
            {hasButton && (
              <>
                <div className="flex flex-col xl:flex-row gap-4 mt-8 items-center justify-start lg:justify-start">
                  <Button 
                    variant={buttonStyle}
                    size="lg"
                    asChild
                    className="w-full sm:w-auto lg:mr-auto"
                  >
                    <Link href={buttonLink ?? ""}>
                      {buttonText}
                    </Link>
                  </Button>
                  {secondaryButton && (
                    <>
                      <span className="text-muted-foreground paragraph-sm lg:mr-auto">or</span>
                      <Button 
                        variant={secondaryButtonStyle}
                        size="lg"
                        asChild
                        className="w-full sm:w-auto lg:mr-auto"
                      >
                        <Link href={secondaryButtonLink ?? ""}>
                          {secondaryButtonText}
                        </Link>
                      </Button>
                    </>
                  )}  
                </div>
              </>
            )}
          </div>
          <div className="w-full shrink lg:w-1/2">
            <div className="grid w-full grid-cols-2 grid-rows-2 gap-4 lg:gap-8">
              <div className="col-[1_/_2] row-[1_/_3]">
                <div className="h-full w-full overflow-hidden rounded-xl sm:rounded-2xl xl:rounded-3xl">
                  {leftImage && (
                    <Image
                      src={leftImage}
                      alt={leftImageAltText || ''}
                      title={leftImageAltText || ''}
                      className="h-full w-full object-cover object-center"
                      width={500}
                      height={500}
                    />
                  )}
                </div>
              </div>
              <div className="col-[2_/_3] row-[1_/_2] aspect-[4/3]">
                <div className="h-full w-full overflow-hidden rounded-xl sm:rounded-2xl xl:rounded-3xl">
                  {rightTopImage && (
                    <Image
                      src={rightTopImage}
                      alt={rightTopImageAltText || ''}
                      title={rightTopImageAltText || ''}
                      className="h-full w-full object-cover object-center"
                      width={500}
                      height={500}
                    />
                  )}
                </div>
              </div>
              <div className="col-[2_/_3] row-[2_/_3] aspect-[4/3]">
                <div className="h-full w-full overflow-hidden rounded-xl sm:rounded-2xl xl:rounded-3xl">
                  {rightBottomImage && (
                    <Image
                      src={rightBottomImage}
                      alt={rightBottomImageAltText || ''}
                      title={rightBottomImageAltText || ''}
                      className="h-full w-full object-cover object-center"
                      width={500}
                      height={500}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {withTable && (
          <TableBlock {...table} /> 
        )}
      </div>
    </SectionWrapper>
  );
};

export { Feature19 };



